import { useMemo, useState, useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

/* ============================================================
 * Shared design system — primitives reused across pages.
 * Brand gradient: var(--color-primary) (teal) → #0984E3 (blue)
 * ========================================================== */

/* ---- Helper: gradient classes used throughout ---- */
export const GRADIENT_BG ="bg-gradient-brand";
export const GRADIENT_TEXT ="bg-gradient-brand-text";
export const GRADIENT_BG_10 ="bg-gradient-brand-10";
export const GRADIENT_BG_5 ="bg-gradient-brand-5";

/* ---- PageShell ---- */
export function PageShell({
 children,
 maxWidth: _maxWidth ="",
}: {
 children: ReactNode;
 maxWidth?: string;
}) {
 const location = useLocation();
 const key = useMemo(() => location.pathname, [location.pathname]);
 return (
 <div className="relative flex flex-1 flex-col w-full min-h-full">
 <div
 key={key}
 className="relative flex-1 w-full px-5 py-5 sm:px-6 lg:px-7 animate-page-enter"style={{ willChange: "opacity, transform"}}
 >
 {children}
 </div>
 </div>
 );
}

/* ---- BackLink ---- */
export function BackLink({
 onClick,
 label ="Back",
}: {
 onClick: () => void;
 label?: string;
}) {
 return (
 <button
 type="button"onClick={onClick}
 className="mb-4 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-slate-500 transition hover:bg-white hover:text-role-primary hover:shadow-sm">
 <svg
 className="h-4 w-4"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M15 18l-6-6 6-6"/>
 </svg>
 {label}
 </button>
 );
}

/* ---- PageHeader ---- */
export function PageHeader({
 eyebrow,
 title,
 subtitle,
 actions,
}: {
 eyebrow?: string;
 title: string;
 subtitle?: ReactNode;
 actions?: ReactNode;
}) {
 return (
 <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
 <div className="min-w-0 flex-1">
 {eyebrow && (
 <p className="text-xs font-semibold uppercase tracking-wider text-role-primary">
 {eyebrow}
 </p>
 )}
 <h1 className="mt-1 text-lg font-bold tracking-tight text-gray-900">
 {title}
 </h1>
 {subtitle && (
 <div className="mt-1 text-sm text-gray-500">
 {subtitle}
 </div>
 )}
 </div>
 {actions && (
 <div className="flex flex-wrap items-center gap-2">{actions}</div>
 )}
 </header>
 );
}

/* ---- SearchInput ---- */
export function SearchInput({
 value,
 onChange,
 placeholder ="Search...",
 className ="w-72",
}: {
 value: string;
 onChange: (v: string) => void;
 placeholder?: string;
 className?: string;
}) {
 return (
 <div className={`relative ${className}`}>
 <svg
 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <circle cx="11"cy="11"r="7"/>
 <path d="m21 21-4.3-4.3"/>
 </svg>
 <input
 type="search"placeholder={placeholder}
 value={value}
 onChange={(e) => onChange(e.target.value)}
 className="w-full rounded-lg border border-slate-200 bg-white h-9 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition focus:border-role-primary focus:outline-none focus:ring-2 focus:ring-emerald-200"/>
 </div>
 );
}

/**
 * DebouncedSearchInput — Same visual as SearchInput but debounces
 * the onChange callback by 300ms. Use this for search filters that
 * trigger expensive operations (API calls, filtering large lists).
 *
 * The input remains responsive (immediate local state update) but
 * the parent only receives the debounced value.
 */
export function DebouncedSearchInput({
 value,
 onChange,
 placeholder ="Search...",
 className ="w-72",
 delay = 300,
}: {
 value: string;
 onChange: (v: string) => void;
 placeholder?: string;
 className?: string;
 delay?: number;
}) {
 const [localValue, setLocalValue] = useState(value);
 const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
 const onChangeRef = useRef(onChange);
 onChangeRef.current = onChange;

 // Sync external value changes (e.g. reset)
 useEffect(() => {
 setLocalValue(value);
 }, [value]);

 const handleChange = (v: string) => {
 setLocalValue(v);
 if (timerRef.current) clearTimeout(timerRef.current);
 timerRef.current = setTimeout(() => {
 onChangeRef.current(v);
 }, delay);
 };

 useEffect(() => {
 return () => {
 if (timerRef.current) clearTimeout(timerRef.current);
 };
 }, []);

 return (
 <div className={`relative ${className}`}>
 <svg
 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <circle cx="11"cy="11"r="7"/>
 <path d="m21 21-4.3-4.3"/>
 </svg>
 <input
 type="search"placeholder={placeholder}
 value={localValue}
 onChange={(e) => handleChange(e.target.value)}
 className="w-full rounded-lg border border-slate-200 bg-white h-9 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition focus:border-role-primary focus:outline-none focus:ring-2 focus:ring-emerald-200"/>
 </div>
 );
}

/* ---- RefreshButton ---- */
export function RefreshButton({
 onClick,
 spinning = false,
}: {
 onClick: () => void;
 disabled?: boolean;
 spinning?: boolean;
}) {
 return (
 <button
 type="button"onClick={onClick}
 disabled={spinning}
 aria-label="Refresh"className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white p-2 text-slate-500 shadow-sm transition hover:bg-slate-50 disabled:opacity-60">
 <svg
 className={`h-4 w-4 ${spinning ?"animate-spin":""}`}
 viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <polyline points="23 4 23 10 17 10"/>
 <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
 </svg>
 </button>
 );
}

/* ---- Avatar ---- */
const AVATAR_STYLE = "bg-[var(--color-primary)]/15 text-[var(--color-primary)]";

export function Avatar({
 name,
 id,
 size ="md",
}: {
 name: string | null | undefined;
 id?: number;
 size?:"sm" | "md" | "lg";
}) {
 const initials = (() => {
 if (!name) return"?";
 const parts = name.trim().split(/\s+/).filter(Boolean);
 if (parts.length === 0) return"?";
 const first = parts[0];
 const last = parts[parts.length - 1];
 if (!first || !last) return"?";
 if (parts.length === 1) return first.charAt(0).toUpperCase();
 return (first.charAt(0) + last.charAt(0)).toUpperCase();
 })();
 void id; // id no longer used for color selection
 const dim =
 size ==="sm"?"h-8 w-8 text-xs": size ==="lg"?"h-14 w-14 text-base":"h-11 w-11 text-sm";
 return (
 <div
 className={`flex shrink-0 items-center justify-center rounded-full font-semibold ring-2 ring-white shadow-sm ${dim} ${AVATAR_STYLE}`}
 aria-hidden="true">
 {initials}
 </div>
 );
}

/* ---- Button ---- */
type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "success";
const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
 primary:`${GRADIENT_BG} text-white shadow-sm hover:shadow-md hover:brightness-105 focus:ring-role-primary`,
 secondary:`border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 focus:ring-slate-300`,
 ghost:`bg-transparent text-slate-600 hover:bg-slate-100 focus:ring-slate-300`,
 danger: "bg-red-500 text-white shadow-sm hover:bg-red-600 focus:ring-red-500",
 success:`${GRADIENT_BG} text-white shadow-sm hover:shadow-md hover:brightness-105 focus:ring-role-primary`,
};

// Shared button layout — h-9 (36px) matches search input height across the site.
const BUTTON_BASE ="inline-flex items-center justify-center gap-1.5 rounded-lg h-9 px-4 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed";

export function Button({
 variant ="primary",
 className ="",
 children,
 ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
 variant?: ButtonVariant;
}) {
 return (
 <button
 type="button"{...rest}
 className={`${BUTTON_BASE} ${BUTTON_VARIANTS[variant]} ${className}`}
 >
 {children}
 </button>
 );
}

/* ---- RoleHero ---- */
export function RoleHero({
 role: _role,
 greeting,
 title,
 subtitle,
 meta,
 actions,
 stats,
}: {
 role?: string;
 greeting?: string;
 title: string;
 subtitle?: string;
 meta?: ReactNode;
 actions?: ReactNode;
 stats?: ReactNode;
}) {
 return (
 <section className="relative mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
 <div className="relative">
 <div className="flex flex-wrap items-start justify-between gap-4">
 <div className="min-w-0 flex-1">
 {greeting && (
 <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-role-primary ring-1 ring-inset ring-emerald-200">
 {greeting}
 </span>
 )}
 <div className="mt-3 flex flex-wrap items-center gap-3">
 <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
 {title}
 </h1>
 {meta}
 </div>
 {subtitle && (
 <p className="mt-1 max-w-2xl text-sm text-slate-500">
 {subtitle}
 </p>
 )}
 </div>
 {actions && (
 <div className="flex flex-wrap items-center gap-2">{actions}</div>
 )}
 </div>
 {stats && (
 <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
 {stats}
 </div>
 )}
 </div>
 </section>
 );
}

/* ---- HeroStat ---- */
export function HeroStat({
 label,
 value,
 hint,
}: {
 label: string;
 value: ReactNode;
 hint?: string;
}) {
 return (
 <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
 <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
 {label}
 </p>
 <p className="mt-1 text-2xl font-bold tracking-tight tabular-nums text-slate-900">
 {value}
 </p>
 {hint && <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p>}
 </div>
 );
}

/* ---- SectionHeader ---- */
export function SectionHeader({
 title,
 description,
 action,
}: {
 title: string;
 description?: string;
 action?: ReactNode;
}) {
 return (
 <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
 <div>
 <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
 {description && (
 <p className="mt-0.5 text-xs text-gray-500">{description}</p>
 )}
 </div>
 {action}
 </div>
 );
}

/* ---- Card ---- */
export function Card({
 children,
 className ="",
 padded = false,
 hoverable = false,
}: {
 children: ReactNode;
 className?: string;
 padded?: boolean;
 hoverable?: boolean;
}) {
 return (
 <div
 className={`overflow-clip rounded-xl border border-slate-200 bg-white shadow-sm transition ${hoverable ?"hover:shadow-md hover:border-role-primary/30":"hover:shadow-md"} ${padded ?"p-5":""} ${className}`}
 >
 {children}
 </div>
 );
}

/* ---- StatCard ---- */
const ACCENT_BG: Record<string, string> = {
 brand: "bg-gradient-brand text-white",
 emerald: "bg-emerald-50 text-role-primary",
 indigo: "bg-emerald-50 text-role-primary",
 rose: "bg-rose-50 text-rose-600",
 slate: "bg-gray-50 text-gray-600",
};

export function StatCard({
 label,
 value,
 hint,
 accent ="slate",
 icon,
}: {
 label: string;
 value: ReactNode;
 hint?: string;
 accent?: string;
 icon?: ReactNode;
}) {
 const valueClass =
 accent ==="brand"? GRADIENT_TEXT
 : accent ==="emerald"?"text-role-primary": accent ==="indigo"?"text-role-primary": accent ==="rose"?"text-rose-600":"text-gray-900";
 return (
 <div className="app-card app-card-hover group relative overflow-hidden p-5">
 <div className="relative flex items-start justify-between gap-3">
 <div className="min-w-0 flex-1">
 <p className="text-xs font-medium text-gray-500">{label}</p>
 <p
 className={`mt-1.5 text-2xl font-bold tracking-tight tabular-nums ${valueClass}`}
 >
 {value}
 </p>
 {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
 </div>
 {icon && (
 <span
 className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${ACCENT_BG[accent] ?? ACCENT_BG.slate}`}
 >
 {icon}
 </span>
 )}
 </div>
 </div>
 );
}

/* ---- ActionTile ---- */
export function ActionTile({
 title,
 description,
 onClick,
 icon,
 badge,
}: {
 title: string;
 description: string;
 onClick: () => void;
 icon: ReactNode;
 badge?: ReactNode;
}) {
 return (
 <button
 type="button"onClick={onClick}
 className="group relative flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:shadow-md hover:border-role-primary/30 focus:outline-none focus:ring-2 focus:ring-role-primary focus:ring-offset-2">
 <div className="relative flex items-start justify-between gap-2">
 <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand-10 text-role-primary ring-1 ring-inset ring-emerald-200">
 {icon}
 </span>
 {badge}
 </div>
 <h3 className="relative mt-3 text-sm font-semibold text-gray-900 group-hover:text-role-primary">
 {title}
 </h3>
 <p className="relative mt-1 text-xs text-gray-500">{description}</p>
 <span className="relative mt-4 inline-flex items-center gap-1 text-xs font-semibold text-role-primary opacity-0 transition group-hover:opacity-100">
 Open{""}
 <svg
 className="h-3 w-3"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M5 12h14M13 5l7 7-7 7"/>
 </svg>
 </span>
 </button>
 );
}

/* ---- LoadingDot ---- */
export function LoadingDot({ label ="Loading…"}: { label?: string }) {
 return (
 <div className="px-6 py-12 text-center">
 <div className="mx-auto mb-3 h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-emerald-500"/>
 <p className="text-xs text-gray-500">{label}</p>
 </div>
 );
}

/* ---- Skeleton ---- */
export function SkeletonCard({ lines = 3 }: { lines?: number }) {
 return (
 <div className="app-card p-5">
 <div className="skeleton mb-3 h-5 w-3/4"/>
 {Array.from({ length: lines }).map((_, i) => (
 <div key={i} className="skeleton mb-2 h-3 w-full"/>
 ))}
 </div>
 );
}

/* ---- PageSkeleton — full page loading state ---- */
export function PageSkeleton() {
 return (
 <div className="px-5 py-5 sm:px-6 lg:px-7 animate-pulse">
 {/* Header skeleton */}
 <div className="mb-6">
 <div className="skeleton h-4 w-20 mb-2"/>
 <div className="skeleton h-7 w-64 mb-1"/>
 <div className="skeleton h-3 w-48"/>
 </div>
 {/* Stats row */}
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
 {Array.from({ length: 4 }).map((_, i) => (
 <div
 key={i}
 className="rounded-xl border border-slate-200 bg-white p-4">
 <div className="skeleton h-3 w-16 mb-2"/>
 <div className="skeleton h-6 w-12"/>
 </div>
 ))}
 </div>
 {/* Table skeleton */}
 <div className="rounded-xl border border-slate-200 bg-white">
 <div className="border-b border-slate-100 px-4 py-3">
 <div className="skeleton h-4 w-32"/>
 </div>
 {Array.from({ length: 5 }).map((_, i) => (
 <div
 key={i}
 className="flex items-center gap-4 border-b border-slate-50 px-4 py-3">
 <div className="skeleton h-8 w-8 rounded-full"/>
 <div className="flex-1">
 <div className="skeleton h-3 w-40 mb-1"/>
 <div className="skeleton h-2 w-24"/>
 </div>
 <div className="skeleton h-5 w-16 rounded-full"/>
 </div>
 ))}
 </div>
 </div>
 );
}

/* ---- TableSkeleton — for inline table loading ---- */
export function TableSkeleton({
 rows = 5,
 cols = 4,
}: {
 rows?: number;
 cols?: number;
}) {
 return (
 <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
 <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
 <div className="flex gap-8">
 {Array.from({ length: cols }).map((_, i) => (
 <div key={i} className="skeleton h-3 w-20"/>
 ))}
 </div>
 </div>
 {Array.from({ length: rows }).map((_, i) => (
 <div
 key={i}
 className="flex items-center gap-8 border-b border-slate-50 px-4 py-3 last:border-0">
 {Array.from({ length: cols }).map((_, j) => (
 <div
 key={j}
 className={`skeleton h-3 ${j === 0 ?"w-32":"w-20"}`}
 />
 ))}
 </div>
 ))}
 </div>
 );
}

/* ---- EmptyHint ---- */
export function EmptyHint({
 title,
 description,
 action,
 icon,
}: {
 title: string;
 description?: string;
 action?: ReactNode;
 icon?: ReactNode;
}) {
 return (
 <div className="px-6 py-12 text-center">
 {icon && (
 <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 text-gray-400 ring-1 ring-inset ring-gray-200">
 {icon}
 </div>
 )}
 <p className="text-sm font-semibold text-gray-900">{title}</p>
 {description && (
 <p className="mx-auto mt-1 max-w-sm text-xs text-gray-500">
 {description}
 </p>
 )}
 {action && <div className="mt-4">{action}</div>}
 </div>
 );
}

/* ---- StatusPill ---- */
const STATUS_PILL_EMERALD ="bg-emerald-50 text-role-primary ring-emerald-200";
const STATUS_PILL_BLUE ="bg-emerald-50 text-role-primary ring-emerald-200";
const STATUS_PILL_ROSE ="bg-rose-50 text-rose-700 ring-rose-600/20";
const STATUS_PILL_BRAND ="bg-gradient-brand-10 text-role-primary ring-emerald-200";
const STATUS_PILL_SLATE ="bg-slate-100 text-slate-600 ring-slate-500/20";

const STATUS_PILL_FAMILY: Record<string, string> = {
 // Emerald family
 approved: STATUS_PILL_EMERALD,
 completed: STATUS_PILL_EMERALD,
 resolved: STATUS_PILL_EMERALD,
 low: STATUS_PILL_EMERALD,
 active: STATUS_PILL_EMERALD,
 employee: STATUS_PILL_EMERALD,
 replied: STATUS_PILL_EMERALD,
 // Blue family
 pending: STATUS_PILL_BLUE,
 paused: STATUS_PILL_BLUE,
 medium: STATUS_PILL_BLUE,
 manager: STATUS_PILL_BLUE,"awaiting reply": STATUS_PILL_BLUE,
 open: STATUS_PILL_BLUE,
 // Rose family
 rejected: STATUS_PILL_ROSE,
 cancelled: STATUS_PILL_ROSE,
 high: STATUS_PILL_ROSE,
 critical: STATUS_PILL_ROSE,"on hold": STATUS_PILL_ROSE,
 hold: STATUS_PILL_ROSE,
 // Brand family
 in_progress: STATUS_PILL_BRAND,
 running: STATUS_PILL_BRAND,
 admin: STATUS_PILL_BRAND,
 // Slate family (defaults)
 closed: STATUS_PILL_SLATE,
 draft: STATUS_PILL_SLATE,
 submitted: STATUS_PILL_SLATE,
 all: STATUS_PILL_SLATE,
};

export function StatusPill({
 status,
 className ="",
}: {
 status: string;
 className?: string;
}) {
 const raw = status ||"";
 const k = raw.toLowerCase().replace(/_/g,"");
 const cls = STATUS_PILL_FAMILY[k] ?? STATUS_PILL_SLATE;
 return (
 <span
 className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset ${cls} ${className}`}
 >
 <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70"/>
 {raw.toLowerCase().replace(/_/g,"")}
 </span>
 );
}

/* ---- DetailSection ---- */
export function DetailSection({
 title,
 action,
 children,
 className ="",
}: {
 title: string;
 action?: ReactNode;
 children: ReactNode;
 className?: string;
}) {
 return (
 <section
 className={`mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}
 >
 <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-3">
 <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
 {action}
 </div>
 <div>{children}</div>
 </section>
 );
}

/* ---- DetailRow ---- */
export function DetailRow({
 label,
 value,
 capitalize = true,
}: {
 label: string;
 value: ReactNode;
 capitalize?: boolean;
}) {
 return (
 <div>
 <dt className="text-xs font-medium text-gray-500">{label}</dt>
 <dd
 className={`mt-0.5 text-sm font-semibold text-gray-900 ${capitalize ?"capitalize":""}`}
 >
 {value}
 </dd>
 </div>
 );
}

/* ---- DetailGrid ---- */
export function DetailGrid({ children }: { children: ReactNode }) {
 return (
 <dl className="grid grid-cols-1 gap-x-6 gap-y-4 px-5 py-4 sm:grid-cols-2">
 {children}
 </dl>
 );
}

/* ---- TabPills ---- */
export function TabPills<T extends string>({
 value,
 onChange,
 items,
}: {
 value: T;
 onChange: (v: T) => void;
 items: ReadonlyArray<{ id: T; label: string }>;
}) {
 return (
 <div className="inline-flex flex-wrap gap-2">
 {items.map((tab) => {
 const active = value === tab.id;
 return (
 <button
 key={tab.id}
 type="button"onClick={() => onChange(tab.id)}
 className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
 active
 ?"bg-gradient-brand text-white shadow-sm":"bg-white text-gray-600 ring-1 ring-inset ring-gray-200 hover:bg-gray-50"}`}
 >
 {tab.label}
 </button>
 );
 })}
 </div>
 );
}

/* ---- Sparkbar ---- */
export function Sparkbar({
 values,
 max,
 className ="",
}: {
 values: number[];
 max?: number;
 className?: string;
}) {
 const peak = max ?? Math.max(1, ...values);
 return (
 <div className={`flex items-end gap-1 ${className}`}>
 {values.map((v, i) => {
 const h = Math.max(2, Math.round((v / peak) * 24));
 return (
 <span
 key={i}
 className="w-2 rounded-sm bg-gradient-brand-to-t opacity-60"style={{ height:`${h}px`}}
 />
 );
 })}
 </div>
 );
}

/* ---- MiniStat ---- */
export function MiniStat({
 label,
 value,
 accent ="slate",
}: {
 label: string;
 value: number;
 accent?:"slate" | "brand" | "emerald" | "indigo";
}) {
 const accentClass =
 accent ==="brand"? GRADIENT_TEXT
 : accent ==="emerald"?"text-role-primary": accent ==="indigo"?"text-role-primary":"text-gray-900";
 return (
 <div className="app-card app-card-hover p-4">
 <p className="text-xs font-medium text-gray-500">{label}</p>
 <p className={`mt-1.5 text-2xl font-bold tracking-tight ${accentClass}`}>
 {value}
 </p>
 </div>
 );
}

/* ---- Th ---- */
export function Th({
 children,
 align ="left",
}: {
 children: ReactNode;
 align?:"left" | "right";
}) {
 return (
 <th
 scope="col"className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 ${align ==="right"?"text-right":"text-left"}`}
 >
 {children}
 </th>
 );
}

/* ---- Td ---- */
export function Td({
 children,
 align ="left",
 className ="",
}: {
 children: ReactNode;
 align?:"left" | "right";
 className?: string;
}) {
 // Check if the content is a placeholder dash (— or --)
 const isPlaceholder = (() => {
 if (typeof children ==="string")
 return children.trim() ==="—"|| children.trim() ==="--";
 if (
 typeof children ==="object"&&
 children !== null &&"props"in (children as any)
 ) {
 const child = children as any;
 if (child.props?.children && typeof child.props.children ==="string") {
 const text = child.props.children.trim();
 return text ==="—"|| text ==="--";
 }
 }
 return false;
 })();

 const alignClass = isPlaceholder
 ?"text-center": align ==="right"?"text-right":"text-left";
 return (
 <td
 className={`whitespace-nowrap px-4 py-3 text-sm ${alignClass} ${className}`}
 >
 {children}
 </td>
 );
}

/* ---- Placeholder (centered dash for missing data) ---- */
export function Placeholder() {
 return <span className="block text-center">—</span>;
}

/* ---- Pagination ---- */
export function Pagination({
 currentPage,
 totalPages,
 onPageChange,
 totalItems,
 pageSize = 10,
}: {
 currentPage: number;
 totalPages: number;
 onPageChange: (page: number) => void;
 totalItems?: number;
 pageSize?: number;
}) {
 if (totalPages <= 1) return null;

 const start = (currentPage - 1) * pageSize + 1;
 const end = Math.min(
 currentPage * pageSize,
 totalItems ?? totalPages * pageSize,
 );

 // Generate page numbers to show (max 5 visible)
 const getPageNumbers = (): (number |"...")[] => {
 if (totalPages <= 5)
 return Array.from({ length: totalPages }, (_, i) => i + 1);
 const pages: (number |"...")[] = [];
 if (currentPage <= 3) {
 pages.push(1, 2, 3, 4,"...", totalPages);
 } else if (currentPage >= totalPages - 2) {
 pages.push(
 1,"...",
 totalPages - 3,
 totalPages - 2,
 totalPages - 1,
 totalPages,
 );
 } else {
 pages.push(
 1,"...",
 currentPage - 1,
 currentPage,
 currentPage + 1,"...",
 totalPages,
 );
 }
 return pages;
 };

 return (
 <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-slate-200 px-4 py-3">
 <span className="text-xs text-slate-500 order-2 sm:order-1">
 {totalItems != null
 ?`Showing ${start}–${end} of ${totalItems}`:`Page ${currentPage} of ${totalPages}`}
 </span>
 <div className="flex items-center gap-1 order-1 sm:order-2">
 {/* Prev */}
 <button
 type="button"disabled={currentPage <= 1}
 onClick={() => onPageChange(currentPage - 1)}
 aria-label="Go to previous page"className="rounded-lg px-2 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">
 ← <span className="hidden sm:inline">Prev</span>
 </button>

 {/* Page numbers */}
 {getPageNumbers().map((p, idx) =>
 p ==="..."? (
 <span
 key={`dot-${idx}`}
 className="px-1 text-xs text-slate-400 hidden sm:inline">
 …
 </span>
 ) : (
 <button
 key={p}
 type="button"onClick={() => onPageChange(p)}
 aria-label={`Go to page ${p}`}
 aria-current={currentPage === p ?"page": undefined}
 className={`min-w-[28px] rounded-lg px-2 py-1.5 text-xs font-medium transition ${
 currentPage === p
 ?"bg-[#0984E3] text-white shadow-sm":"text-slate-600 hover:bg-slate-100"} ${totalPages > 5 ?"hidden sm:block":""}`}
 >
 {p}
 </button>
 ),
 )}

 {/* Mobile page indicator */}
 {totalPages > 5 && (
 <span className="sm:hidden text-xs text-slate-500 px-2">
 {currentPage}/{totalPages}
 </span>
 )}

 {/* Next */}
 <button
 type="button"disabled={currentPage >= totalPages}
 onClick={() => onPageChange(currentPage + 1)}
 aria-label="Go to next page"className="rounded-lg px-2 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">
 <span className="hidden sm:inline">Next</span> →
 </button>
 </div>
 </div>
 );
}

/* ---- ResponsiveTable ---- */
/**
 * Wraps a table with responsive behavior:
 * - Horizontal scroll on small screens
 * - Optional sticky header
 * - Consistent styling
 */
export function ResponsiveTable({
 children,
 stickyHeader = true,
 className ="",
}: {
 children: ReactNode;
 stickyHeader?: boolean;
 className?: string;
}) {
 return (
 <div
 className={`table-responsive ${stickyHeader ?"table-sticky-header":""} ${className}`}
 role="region"aria-label="Scrollable table"tabIndex={0}
 >
 {children}
 </div>
 );
}

